import { useState, useRef, useEffect } from "react";
import {
  BOOKING_STEPS,
  STEP_TIMEOUTS,
} from "../Helpers/bookShipmentBookingSteps";
import type { CompletionResult } from "../BookShipmentProcessingScreen";

interface UseBookingProgressOptions {
  onComplete?: ((result: CompletionResult) => void) | undefined;
  isExternalComplete?: boolean | undefined;
}

export function useBookShipmentBookingProgress(
  options?: UseBookingProgressOptions,
) {
  const { onComplete, isExternalComplete } = options || {};

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [currentPercentage, setCurrentPercentage] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);

  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const hasCompletedRef = useRef(false);

  const getRandomPercentage = (stepIndex: number): number => {
    const step = BOOKING_STEPS[stepIndex];
    if (step === undefined) {
      return 0;
    }
    const randomIndex = Math.floor(Math.random() * step.percentageRange.length);
    return step.percentageRange[randomIndex] ?? 0;
  };

  const clearAllTimeouts = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  };

  const completeProgress = (result: CompletionResult) => {
    if (hasCompletedRef.current) return;
    hasCompletedRef.current = true;
    setIsCompleted(true);
    onComplete?.(result);
  };

  const advanceToNextStep = (stepIndex: number) => {
    if (stepIndex >= BOOKING_STEPS.length) {
      completeProgress({ completedByAnimation: true });
      return;
    }

    const targetPercentage = getRandomPercentage(stepIndex);
    setCurrentStepIndex(stepIndex);
    setCurrentPercentage(targetPercentage);

    const timeout =
      stepIndex < BOOKING_STEPS.length - 1 ? STEP_TIMEOUTS[stepIndex] : 800;

    timeoutRef.current = setTimeout(() => {
      if (stepIndex < BOOKING_STEPS.length - 1) {
        advanceToNextStep(stepIndex + 1);
      } else {
        completeProgress({ completedByAnimation: true });
      }
    }, timeout);
  };

  useEffect(() => {
    if (isExternalComplete) {
      clearAllTimeouts();

      // Jump to final step
      setCurrentStepIndex(BOOKING_STEPS.length - 1);
      setCurrentPercentage(100);
      setIsCompleted(true);

      // Call parent callback only if animation hasn't already called it
      if (!hasCompletedRef.current) {
        hasCompletedRef.current = true;
        // Allow the animation to finish before sending onComplete event
        setTimeout(() => {
          onComplete?.({ completedByAnimation: false });
        }, 1_000);
      }
    }
  }, [isExternalComplete]);

  useEffect(() => {
    advanceToNextStep(0);

    return clearAllTimeouts;
  }, []);

  const currentStep = BOOKING_STEPS[currentStepIndex];

  return {
    currentStep,
    currentStepIndex,
    currentPercentage,
    isCompleted,
    allSteps: BOOKING_STEPS,
  };
}
